// src/modules/bookLocation/keyHandover/pages/existingSubscription/SubscriptionLoginPage.tsx

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../../components/ui/spinner";
import { Input } from "../../../../../components/ui/input";
import { Button } from "../../../../../components/ui/button";
import {
    cleanEmail,
    getEmailSuggestion,
    isEmailAcceptable,
} from "../../../../../util/validation";
import routes from "../../../../../routes/routes";
import { useLocationHydration } from "../../hooks/useLocationHydration";
import { isManageIntent } from "../../common/subscriptionManage";
import { useAppDispatch } from "../../../../../redux/useDispatch";
import { useInitiateCustomerAgreementVerificationMutation } from "../../../../../api/payments/paymentsApiSlice";
import {
    setHasExistingSubscription,
    setExistingSubscriptionEmail,
    setExistingSubscriptionVerified,
    setExistingSubscriptionToken,
    setExistingSubscriptionCustomerAgreementId,
} from "../../../../../redux/slice/subscription/subscriptionSlice";

/**
 * Login Content
 * Displays email input form
 */
function LoginContent({
    email,
    emailError,
    isSendingCode,
    onEmailChange,
}: {
    email: string;
    emailError: string | null;
    isSendingCode: boolean;
    onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
    const { t } = useTranslation();
    const locationHydrated = useLocationHydration();

    if (!locationHydrated) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 max-w-lg mx-auto">
            {/* Intro text */}
            <div className="text-center space-y-2">
                <p className="text-sm text-slate-600">
                    {t("book_location.keys.subscription_login.description", {
                        defaultValue:
                            "Geben Sie die E-Mail-Adresse Ihres Abonnements ein. Wir senden Ihnen einen Login-Code.",
                    })}
                </p>
            </div>

            {/* Email input */}
            <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-slate-700">
                    {t("book_location.keys.subscription_login.label", {
                        defaultValue: "E-Mail-Adresse",
                    })}
                </label>
                <Input
                    id="email"
                    type="email"
                    placeholder="name@beispiel.de"
                    value={email}
                    onChange={onEmailChange}
                    error={Boolean(emailError)}
                    disabled={isSendingCode}
                    className="w-full"
                />
                {emailError && (
                    <p className="text-sm text-red-600 mt-1">{emailError}</p>
                )}
            </div>
        </div>
    );
}

/**
 * Subscription Login Page
 * 
 * Flow 3: Existing Subscription - Step 1
 * User enters email to login with existing subscription
 */
const SubscriptionLoginPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const [initiateVerification] = useInitiateCustomerAgreementVerificationMutation();

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState<string | null>(null);
    const [isSendingCode, setIsSendingCode] = useState(false);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        // Clear error when user starts typing; full validation runs on send
        setEmailError(null);
    };

    const handleSendLoginCode = async () => {
        // Validate email
        const cleanedEmail = cleanEmail(email);
        if (!cleanedEmail) {
            setEmailError(
                t("book_location.keys.subscription_login.error_required", {
                    defaultValue: "E-Mail ist erforderlich",
                })
            );
            return;
        }

        const suggestion = getEmailSuggestion(cleanedEmail);
        if (suggestion) {
            setEmailError(
                t("book_location.personal_data.validation.email_typo_suggestion", {
                    suggestion,
                })
            );
            return;
        }

        if (!isEmailAcceptable(cleanedEmail)) {
            setEmailError(
                t("book_location.keys.subscription_login.error_invalid", {
                    defaultValue: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
                })
            );
            return;
        }

        setIsSendingCode(true);

        try {
            // The agreement id arrives with the confirmed code, not here.
            await initiateVerification({ email: cleanedEmail }).unwrap();

            // Persist flow + email in state
            dispatch(setHasExistingSubscription("existing"));
            dispatch(setExistingSubscriptionEmail(cleanedEmail));
            dispatch(setExistingSubscriptionVerified(false));
            dispatch(setExistingSubscriptionToken(null));
            dispatch(setExistingSubscriptionCustomerAgreementId(null));

            // Build URL search params for verify step
            const params = new URLSearchParams(location.search);
            params.set("subscriptionFlow", "existing");
            params.set("email", cleanedEmail);
            params.delete("customerAgreementId");

            // Navigate to verify page
            // For existing subscription, we use the Flow 3 verify path
            navigate({
                pathname: routes.keyHandoverSubscriptionVerifyPath,
                search: `?${params.toString()}`,
            });
        } catch (error: any) {
            const apiMessage = error?.data?.message || error?.message;
            setEmailError(
                apiMessage ||
                    t("book_location.keys.subscription_login.error_send_failed", {
                        defaultValue: "Fehler beim Senden des Codes. Bitte versuchen Sie es erneut.",
                    })
            );
        } finally {
            setIsSendingCode(false);
        }
    };

    const bottomActions = (
        <Button
            size="lg"
            variant="primary"
            className="w-full"
            disabled={isSendingCode || !email.trim()}
            onClick={handleSendLoginCode}
        >
            {isSendingCode ? (
                <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t("book_location.keys.subscription_login.sending", {
                        defaultValue: "Sende Code...",
                    })}
                </>
            ) : (
                t("book_location.keys.subscription_login.send_button", {
                    defaultValue: "Login-Code senden",
                })
            )}
        </Button>
    );

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.subscription_login.header_text", {
                defaultValue: "Abo-Login",
            })}
            // Reached from Infos ("Verträge hier kündigen") without a location:
            // back leads there, not into the booking flow.
            backTo={isManageIntent(location.search) ? routes.hints : routes.keyHandoverPaymentModelPath}
            bottomActions={bottomActions}
        >
            <KeyHandoverContentWrapper>
                <LoginContent
                    email={email}
                    emailError={emailError}
                    isSendingCode={isSendingCode}
                    onEmailChange={handleEmailChange}
                />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default SubscriptionLoginPage;
